import * as React from 'react';
import { JobData } from './JobsContent';
import { OperationContext } from './OperationContext';
import { Operations } from './NavigatorData';
import { Divider, Theme, createStyles, WithStyles, withStyles, Typography } from '@material-ui/core';
import { ArrayAlignment, ThreeWayMergeMode, PatchDirection, GraftResolutionMode } from '../api/commonConfig';
import { DeltaXMLColors } from '../styling/DeltaColors';

const styles = (theme: Theme) =>
    createStyles({
        container : {
          width: '839px'
        },
        divider: {
          margin: '3px',
          backgroundColor: DeltaXMLColors.GREY,
        },
        heading: {
          margin: '3px'
        }
    });

export interface JobMetadataProps extends WithStyles<typeof styles> {
  jobData: JobData;
}

const getHumanReadableStringForArrayAlignmentOption = (option: ArrayAlignment) => {
  let optionLabel = '';
  switch (option) {
    case ArrayAlignment.orderless:
      optionLabel = 'Orderless';
      break;
    case ArrayAlignment.positionPriority:
      optionLabel = 'Position';
      break;
    case ArrayAlignment.typeWithValuePriority:
      optionLabel = 'Type With Value';
      break;
  }
  return optionLabel;
}

const getHumanReadableStringForConflictsResolutionOption = (option: ThreeWayMergeMode) => {
  let optionLabel = '';
  switch (option) {
    case ThreeWayMergeMode.showConflicts:
      optionLabel = 'Show';
      break;
    case ThreeWayMergeMode.resolveUsingEdit1:
      optionLabel = 'Resolve with input A';
      break;
    case ThreeWayMergeMode.resolveUsingEdit2:
      optionLabel = 'Resolve with input B';
      break;
  }
  return optionLabel;
}

const getHumanReadableStringForGraftResolutionModeOption = (option: GraftResolutionMode) => {
  let optionLabel = '';
  switch (option) {
    case GraftResolutionMode.changesetPriority:
      optionLabel = 'Changeset Priority';
      break;
    case GraftResolutionMode.targetPriority:
      optionLabel = 'Target Priority';
      break;
    case GraftResolutionMode.additionsOnly:
      optionLabel = 'Additions Only';
      break;
  }
  return optionLabel;
}

function JobMetadata(props: JobMetadataProps) {
  const { classes, jobData } = props;
  const { parameters } = jobData;
  const operationType = React.useContext(OperationContext).operationType;
  let tableBody: JSX.Element = <></>;

  switch (operationType) {
    case Operations.compare:
      tableBody = (<tbody style={{color:'#3B495B'}}>
        <tr>
          <td>{parameters.inputAKBytes ? 'Input A (KB)' : 'Input A'}</td>
          <td>{parameters.inputAKBytes ? parameters.inputAKBytes.toFixed(1) : parameters.inputA}</td>
          <td>Word By Word</td>
          <td>{parameters.wordByWordOption === true ? 'On' : 'Off'}</td>
        </tr>
        <tr>
          <td>{parameters.inputBKBytes ? 'Input B (KB)' : 'Input B'}</td>
          <td>{parameters.inputBKBytes ? parameters.inputBKBytes.toFixed(1) : parameters.inputB}</td>
          <td>Array Alignment</td>
          <td>{getHumanReadableStringForArrayAlignmentOption(parameters.arrayAlignmentOption!)}</td>
        </tr>
      </tbody>)
      break;
    case Operations.twoWayMerge:
      tableBody = (<tbody style={{color:'#3B495B'}}>
        <tr>
          <td>{parameters.inputAKBytes ? 'Input A (KB)' : 'Input A'}</td>
          <td>{parameters.inputAKBytes ? parameters.inputAKBytes.toFixed(1) : parameters.inputA}</td>
          <td>Word By Word</td>
          <td>{parameters.wordByWordOption === true ? 'On' : 'Off'}</td>
        </tr>
        <tr>
          <td>{parameters.inputBKBytes ? 'Input B (KB)' : 'Input B'}</td>
          <td>{parameters.inputBKBytes ? parameters.inputBKBytes.toFixed(1) : parameters.inputB}</td>
          <td>Array Alignment</td>
          <td>{getHumanReadableStringForArrayAlignmentOption(parameters.arrayAlignmentOption!)}</td>
        </tr>
        <tr>
          <td></td>
          <td></td>
          <td>Merge Priority</td>
          <td>{parameters.mergePriorityOption?.toUpperCase()}</td>
        </tr>
      </tbody>)
      break;
    case Operations.threeWayMerge:
      tableBody = (<tbody style={{color:'#3B495B'}}>
        <tr>
          <td>{parameters.inputAKBytes ? 'Input A (KB)' : 'Input A'}</td>
          <td>{parameters.inputAKBytes ? parameters.inputAKBytes.toFixed(1) : parameters.inputA}</td>
          <td>Word By Word</td>
          <td>{parameters.wordByWordOption === true ? 'On' : 'Off'}</td>
        </tr>
        <tr>
          <td>{parameters.inputBKBytes ? 'Input B (KB)' : 'Input B'}</td>
          <td>{parameters.inputBKBytes ? parameters.inputBKBytes.toFixed(1) : parameters.inputB}</td>
          <td>Array Alignment</td>
          <td>{getHumanReadableStringForArrayAlignmentOption(parameters.arrayAlignmentOption!)}</td>
        </tr>
        <tr>
          <td>{parameters.ancestorKBytes ? 'Ancestor (KB)' : 'Ancestor'}</td>
          <td>{parameters.ancestorKBytes ? parameters.ancestorKBytes.toFixed(1) : parameters.ancestor}</td>
          <td>Conflicts Resolution</td>
          <td>{getHumanReadableStringForConflictsResolutionOption(parameters.threeWayMergeModeOption!)}</td>
        </tr>
      </tbody>)
      break;
    case Operations.graft:
      tableBody = (<tbody style={{color:'#3B495B'}}>
        <tr>
          <td>{parameters.inputAKBytes ? 'Changeset (KB)' : 'Changeset'}</td>
          <td>{parameters.inputAKBytes ? parameters.inputAKBytes.toFixed(1) : parameters.inputA}</td>
          <td>Resolution Mode</td>
          <td>{getHumanReadableStringForGraftResolutionModeOption(parameters.graftResolutionModeOption!)}</td>
        </tr>
        <tr>
          <td>{parameters.inputBKBytes ? 'Target Input (KB)' : 'Target Input'}</td>
          <td>{parameters.inputBKBytes ? parameters.inputBKBytes.toFixed(1) : parameters.inputB}</td>
          <td></td>
          <td></td>
        </tr>
      </tbody>)
      break;
    case Operations.patch:
      tableBody = (<tbody style={{color:'#3B495B'}}>
        <tr>
          <td>{parameters.inputAKBytes ? 'Input A (KB)' : 'Input A'}</td>
          <td>{parameters.inputAKBytes ? parameters.inputAKBytes.toFixed(1) : parameters.inputA}</td>
          <td>Patch Direction</td>
          <td>{parameters.patchDirectionOption === PatchDirection.bToa ? 'B to A' : 'A to B'}</td>
        </tr>
        <tr>
          <td>{parameters.inputBKBytes ? 'Input B (KB)' : 'Input B'}</td>
          <td>{parameters.inputBKBytes ? parameters.inputBKBytes.toFixed(1) : parameters.inputB}</td>
          <td></td>
          <td></td>
        </tr>
      </tbody>)
      break;
    default:
      break;
  }

  return (
    <div className={classes.container}>
      <Typography variant="body1" color="primary" className={classes.heading}>{operationType} settings</Typography>
      <Divider className={classes.divider}/>
      <table className="standaloneTable">
        <colgroup>
          <col width="210px" />
          <col width="210px" />
          <col width="210px" />
          <col width="210px" />
        </colgroup>
        {tableBody}
      </table>
    </div>
  )

}

export default withStyles(styles)(JobMetadata);